import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Sans:wght@300;400;500&display=swap');
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
`;

const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Section = styled.section`
  position: relative;
  background: #ffffff;
  padding: 100px 0;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 2;
`;

const Headline = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  text-align: center;
  color: #3b3838;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(30px);

  span {
    color: #a855f7;
  }
`;

const Subheadline = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 1rem;
  text-align: center;
  color: #a8a2a2;
  max-width: 600px;
  margin: 0 auto 3rem;
  opacity: 0;
  transform: translateY(20px);
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
  text-align: center;
  margin-bottom: 3rem;
  opacity: 0;
  transform: translateY(20px);
`;

const Stat = styled.div`
  font-family: "Mendl", sans-serif;
  h3 {
    font-size: 2rem;
    color: #a855f7;
    margin: 0;
  }
  p {
    font-size: 0.8rem;
    color: #7e7777;
    margin: 0;
  }
`;

const ShieldWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 3rem 0;
  opacity: 0;
  transform: scale(0.9);
`;

const Shield = styled.div`
  width: 200px;
  height: 200px;
  background: linear-gradient(
    160deg,
    rgba(124, 58, 237, 0.3),
    rgba(168, 85, 247, 0.1)
  );
  clip-path: polygon(50% 0%, 100% 15%, 100% 60%, 50% 100%, 0% 60%, 0% 15%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    clip-path: polygon(50% 0%, 100% 15%, 100% 60%, 50% 100%, 0% 60%, 0% 15%);
    background: radial-gradient(
      circle at 30% 20%,
      rgba(168, 85, 247, 0.2),
      transparent
    );
  }
`;

const Ring = styled.div`
  position: absolute;
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 50%;
  animation: ${rotateSlow} ${(props) => props.duration || 20}s linear infinite;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  padding: 1.5rem;
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.02);
  opacity: 0;
  transform: translateY(40px);

  &:hover {
    border-color: rgba(168, 85, 247, 0.3);
    background: rgba(168, 85, 247, 0.03);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-family: "Mendl", sans-serif;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #3b3838;
`;

const CardDesc = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 0.85rem;
  color: rgb(136, 134, 134);
  line-height: 1.5;
`;

const Quote = styled.div`
  border-top: 1px solid rgba(168, 85, 247, 0.15);
  padding: 2rem;
  margin-top: 2rem;
  text-align: center;
  opacity: 0;
  transform: translateY(40px);
  font-family: "Mendl", sans-serif;

  p {
    font-style: italic;
    color: rgb(104, 99, 99);
    max-width: 600px;
    margin: 0 auto 1rem;
  }

  h4 {
    color: #a855f7;
    margin: 0;
  }
`;

const FEATURES = [
  {
    icon: "👻",
    title: "Disappearing Photos",
    desc: "Photos vanish after being seen. No screenshots, no traces.",
  },
  {
    icon: "🔐",
    title: "End-to-End Encryption",
    desc: "AES-256 encryption. Not even we can read your messages.",
  },
  {
    icon: "🎭",
    title: "Identity Verification",
    desc: "Every profile verified. No bots, no fakes.",
  },
  {
    icon: "🛡️",
    title: "Screenshot Detection",
    desc: "Instant notification if someone tries to screenshot.",
  },
];

export default function CybersecuritySection() {
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const statsRef = useRef(null);
  const shieldRef = useRef(null);
  const cardRefs = useRef([]);
  const quoteRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Headline animations
      gsap.to(headlineRef.current, {
        scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
        opacity: 1,
        y: 0,
        duration: 0.8,
      });
      gsap.to(subRef.current, {
        scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.2,
      });
      gsap.to(statsRef.current, {
        scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.3,
      });
      gsap.to(shieldRef.current, {
        scrollTrigger: { trigger: shieldRef.current, start: "top 85%" },
        opacity: 1,
        scale: 1,
        duration: 0.6,
      });
      gsap.to(quoteRef.current, {
        scrollTrigger: { trigger: quoteRef.current, start: "top 85%" },
        opacity: 1,
        y: 0,
        duration: 0.6,
      });

      // Cards stagger
      cardRefs.current.forEach((card, i) => {
        gsap.to(card, {
          scrollTrigger: { trigger: card, start: "top 90%" },
          opacity: 1,
          y: 0,
          duration: 0.5,
          delay: i * 0.1,
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Section ref={sectionRef}>
        <Container>
          <Headline ref={headlineRef}>
            Security isn't a feature.
            <br />
            <span>It's the foundation.</span>
          </Headline>
          <Subheadline ref={subRef}>
            Every interaction wrapped in layers of protection. Because when
            trust is everything, half-measures aren't an option.
          </Subheadline>

          <StatsRow ref={statsRef}>
            <Stat>
              <h3>10+</h3>
              <p>Years CEO Experience</p>
            </Stat>
            <Stat>
              <h3>40+</h3>
              <p>Years Dev Team Experience</p>
            </Stat>
            <Stat>
              <h3>256-bit</h3>
              <p>AES Encryption</p>
            </Stat>
          </StatsRow>

          <ShieldWrapper ref={shieldRef}>
            <div style={{ position: "relative" }}>
              <Ring size="260px" duration="25" />
              <Ring size="200px" duration="18" />
              <Shield>🛡️</Shield>
            </div>
          </ShieldWrapper>

          <Grid>
            {FEATURES.map((f, i) => (
              <Card key={i} ref={(el) => (cardRefs.current[i] = el)}>
                <CardIcon>{f.icon}</CardIcon>
                <CardTitle>{f.title}</CardTitle>
                <CardDesc>{f.desc}</CardDesc>
              </Card>
            ))}
          </Grid>

          <Quote ref={quoteRef}>
            <p>
              "I've spent over a decade breaking into systems so I could learn
              how to build ones that can't be broken."
            </p>
            <h4>— Our CEO, Head of Security Architecture</h4>
          </Quote>
        </Container>
      </Section>
    </>
  );
}
